import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../contexts/authStore'
import BrandMark from '../common/BrandMark'
import CustomerNavigation from './CustomerNavigation'

const pageTitles: Record<string, string> = {
  '/customer': 'übersicht',
  '/customer/apps': 'meine apps',
  '/customer/invoices': 'rechnungen',
  '/customer/support': 'support',
  '/customer/account': 'konto',
  '/customer/sessions': 'sitzungen',
}

export default function CustomerLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuthStore()
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()

  const breadcrumb = pageTitles[location.pathname] || 'kundenportal'

  return (
    <div className="wc-theme" style={{ height: '100vh', overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: collapsed ? '76px 1fr' : '240px 1fr',
          height: '100%',
          transition: 'grid-template-columns 0.15s ease',
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            borderRight: '1px solid var(--wc-divider)',
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px 22px',
              gap: 8,
            }}
          >
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--wc-text)',
                fontSize: 15,
                textDecoration: 'none',
                overflow: 'hidden',
              }}
            >
              <BrandMark size={18} />
              {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>Werkstatt-Connect</span>}
            </Link>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="wc-btn wc-btn-secondary wc-btn-icon"
              style={{ width: 28, height: 28, flexShrink: 0 }}
              title={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}
              aria-label={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {!collapsed && (
            <div className="wc-mono wc-muted" style={{ fontSize: 11, letterSpacing: '.06em', padding: '4px 12px', marginBottom: 2 }}>
              KUNDENPORTAL
            </div>
          )}

          <CustomerNavigation collapsed={collapsed} />

          <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--wc-divider)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
              <span
                aria-hidden="true"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: 'var(--wc-accent-800)',
                  color: 'var(--wc-accent-100)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {user?.email?.charAt(0).toUpperCase()}
              </span>
              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email}
                  </div>
                  <div className="wc-muted" style={{ fontSize: 12 }}>Kunde</div>
                </div>
              )}
            </div>
            <Link
              to="/"
              className="wc-sidenav-link"
              style={{ fontSize: 13, color: 'color-mix(in srgb, var(--wc-text) 55%, transparent)' }}
              title={collapsed ? 'Zur Website' : undefined}
            >
              <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm88,104a87.61,87.61,0,0,1-3.33,24H174.16a157.44,157.44,0,0,0,0-48h38.51A87.61,87.61,0,0,1,216,128ZM102,168H154a115.11,115.11,0,0,1-26,45A115.27,115.27,0,0,1,102,168Zm-3.9-16a140.84,140.84,0,0,1,0-48h59.88a140.84,140.84,0,0,1,0,48ZM40,128a87.61,87.61,0,0,1,3.33-24H81.84a157.44,157.44,0,0,0,0,48H43.33A87.61,87.61,0,0,1,40,128ZM154,88H102a115.11,115.11,0,0,1,26-45A115.27,115.27,0,0,1,154,88Zm52.33,0H170.7a135.28,135.28,0,0,0-22.3-45.6A88.29,88.29,0,0,1,206.37,88ZM107.6,42.4A135.28,135.28,0,0,0,85.3,88H49.63A88.29,88.29,0,0,1,107.6,42.4ZM49.63,168H85.3a135.28,135.28,0,0,0,22.3,45.6A88.29,88.29,0,0,1,49.63,168Zm98.77,45.6a135.28,135.28,0,0,0,22.3-45.6h35.67A88.29,88.29,0,0,1,148.4,213.6Z" />
              </svg>
              {!collapsed && <span>Zur Website</span>}
            </Link>
            <button
              type="button"
              onClick={logout}
              className="wc-sidenav-link"
              style={{ fontSize: 13, color: '#fca5a5', width: '100%', textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit' }}
              title={collapsed ? 'Abmelden' : undefined}
            >
              <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M124,216a8,8,0,0,1-8,8H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32h68a8,8,0,0,1,0,16H48V208h68A8,8,0,0,1,124,216Zm109.66-93.66-40-40a8,8,0,0,0-11.32,11.32L212.69,124H108a8,8,0,0,0,0,16H212.69l-30.35,30.34a8,8,0,0,0,11.32,11.32l40-40A8,8,0,0,0,233.66,122.34Z" />
              </svg>
              {!collapsed && <span>Abmelden</span>}
            </button>
            {!collapsed && (
              <div className="wc-mono wc-muted" style={{ fontSize: 10, padding: '10px 12px 0' }}>
                v{__APP_VERSION__}
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 32px',
              borderBottom: '1px solid var(--wc-divider)',
              position: 'sticky',
              top: 0,
              background: 'color-mix(in srgb, var(--wc-bg) 88%, transparent)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 5,
            }}
          >
            <div className="wc-mono wc-muted" style={{ fontSize: 12 }}>portal / {breadcrumb}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Link to="/customer/support" className="wc-btn wc-btn-secondary" style={{ padding: '7px 16px', fontSize: 13 }}>
                Support kontaktieren
              </Link>
              <Link to="/apps" className="wc-btn wc-btn-primary" style={{ padding: '7px 16px', fontSize: 13 }}>
                Apps entdecken
              </Link>
            </div>
          </div>

          <main style={{ flex: 1, minHeight: 0, padding: 32, overflowY: 'auto' }}>{children}</main>
        </div>
      </div>
    </div>
  )
}
